import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { HERO } from "@/constants/testIds";
import ParticleField from "./ParticleField";
import BrandConstellation from "./BrandConstellation";

export default function Hero() {
  const { t } = useLang();

  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative min-h-[100vh] w-full overflow-hidden pt-28 md:pt-36 pb-24 md:pb-32"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-[#050810]" />
      <div className="absolute inset-0 grid-mask opacity-60" />
      <ParticleField />
      <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-[#050810] pointer-events-none" />

      {/* halos */}
      <div className="halo w-[520px] h-[520px] bg-[#007AFF]/25 -top-40 -left-32 animate-pulse-glow" />
      <div className="halo w-[520px] h-[520px] bg-[#00F0FF]/20 -bottom-52 -right-32 animate-pulse-glow" style={{ animationDelay: "1.5s" }} />

      {/* Decorative brand constellation */}
      <div className="hidden lg:block absolute top-24 right-4 xl:right-16 pointer-events-none" aria-hidden="true">
        <BrandConstellation size={520} />
      </div>
      <div className="hidden lg:block absolute top-40 right-20 xl:right-36 w-[220px] xl:w-[260px] h-[220px] xl:h-[260px] bg-[#007AFF]/20 rounded-full blur-3xl pointer-events-none animate-pulse-glow" />

      {/* Mobile constellation (smaller, above fold) */}
      <div className="lg:hidden absolute top-20 right-[-80px] opacity-50 pointer-events-none" aria-hidden="true">
        <BrandConstellation size={360} />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/70"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#00F0FF] opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00F0FF]" />
            </span>
            <span>{t.hero.eyebrow}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-6 font-display text-[44px] leading-[1.02] sm:text-6xl lg:text-[76px] font-light tracking-tighter text-white"
          >
            {t.hero.title_a}
            <br />
            <span className="accent-gradient-text font-medium">{t.hero.title_b}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-6 max-w-2xl text-base sm:text-lg text-[#8B95A5] leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <a
              href="#platforms"
              data-testid={HERO.ctaPrimary}
              className="group inline-flex items-center gap-2 rounded-full btn-primary px-6 py-3 text-sm font-semibold"
            >
              {t.hero.cta_primary}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180" />
            </a>
            <a
              href="#contact"
              data-testid={HERO.ctaSecondary}
              className="inline-flex items-center gap-2 rounded-full btn-ghost px-6 py-3 text-sm font-medium"
            >
              <Sparkles className="h-4 w-4 text-[#00F0FF]" />
              {t.hero.cta_secondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.7 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/50"
          >
            {[t.hero.badge_a, t.hero.badge_b, t.hero.badge_c].map((b) => (
              <span key={b} className="flex items-center gap-2">
                {b}
              </span>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom spacing */}
      <div className="relative z-10 mt-24" />
    </section>
  );
}
